import { AsyncSelect } from 'chakra-react-select'
import classNames from 'classnames'
import React from 'react'
import List from 'src/components/list/List'
import ListItem from 'src/components/list/ListItem'
import { Location } from 'src/types/schema/graphql'

import * as CC from '@chakra-ui/react'
import * as HeroIcons from '@heroicons/react/solid'

import { asyncSelectConfig } from '../../config/asyncSelectConfig'
import { mapStore } from '../../state/mapStore'
import { formatOptionLabel } from '../forYou/functions/formatOptionLabel'
import { guidGenerator } from '../../utils/functions'

const RouteStopsList: React.FC = () => {
    const directionCoordinates = mapStore((state) => state.directionCoordinates)
    const setDirectionsCoordinates = mapStore((state) => state.setDirectionsCoordinates)
    const setShowStopAdd = mapStore((state) => state.setShowStopAdd)
    const getAddressFromCoords = mapStore((state) => state.getAddressFromCoords)
    const defaultCombinedRouteInputData = mapStore((state) => state.defaultCombinedRouteInputData)

    const handleAddressChange = async (
        e: {
            label: string
            value: string
            coordinates: number[]
            location?: Location
        },
        index
    ) => {
        const newDirCoords = [...directionCoordinates]

        if (e && e.coordinates) {
            await getAddressFromCoords(e.coordinates[1], e.coordinates[0])
                .then((res) => {
                    newDirCoords[index] = {
                        lon: e?.coordinates[0],
                        lat: e?.coordinates[1],
                        title: e?.value,
                        placeName: res,
                        location: e?.location,
                    }
                    setDirectionsCoordinates({
                        reorderedArray: newDirCoords,
                        action: 'reorder',
                    })
                })
                .catch(() =>
                    console.error(
                        'There has been an error getting the address from the coordinates in RouteStopsList.tsx'
                    )
                )
        }
        setShowStopAdd(false)
    }

    const showDeleteButtons = directionCoordinates.length > 2
    const { routeStopsConfig } = asyncSelectConfig

    return (
        <List
            id='list'
            items={directionCoordinates}
            onChange={(reorderedArray) =>
                setDirectionsCoordinates({
                    reorderedArray,
                    action: 'reorder',
                })
            }
            customClasses={
                'mt-4 px-4 route-stop flex overflow-y-auto flex-col-reverse min-h-16 h-fit max-h-32 lg:max-h-64 lg:h-fit'
            }
        >
            {directionCoordinates.map((routeStop, index) => {
                const listItem = {
                    content: routeStop?.title,
                    placeName: routeStop?.placeName,
                }
                const stopToUse = {
                    coordinates: [routeStop.lon, routeStop.lat],
                    id: routeStop.placeName,
                    label: routeStop.title,
                    value: routeStop.title,
                    type: 'place',
                    location: routeStop?.location,
                }
                const uniqueKey = `${routeStop.lon}-${routeStop.lat}-${index}`

                return (
                    <ListItem
                        index={index}
                        sortable={true}
                        key={uniqueKey}
                        customClasses='flex'
                        item={listItem}
                        draggableKey={uniqueKey}
                    >
                        <div className='select-decoration--container relative flex w-full items-center sm:max-h-64'>
                            <AsyncSelect
                                {...routeStopsConfig}
                                name={`search-locations-${index}`}
                                placeholder={index === 0 ? `Your location` : `Add a stop`}
                                onChange={(e: {
                                    label: string
                                    value: string
                                    coordinates: number[]
                                    location?: Location
                                }) => handleAddressChange(e, index)}
                                formatOptionLabel={formatOptionLabel}
                                components={{ DropdownIndicator: () => null }}
                                instanceId={`search-locations-stop-${index}`}
                                value={stopToUse}
                                defaultOptions={defaultCombinedRouteInputData}
                                openMenuOnClick={true}
                                styles={{
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                }}
                                menuPortalTarget={document.body}
                            />

                            <CC.IconButton
                                as={HeroIcons.XCircleIcon}
                                size='md'
                                className={classNames(
                                    'max-w-[2rem] cursor-pointer border-none text-gray-300 hover:bg-transparent hover:text-gray-200',
                                    !showDeleteButtons ? 'hidden' : ''
                                )}
                                onClick={() =>
                                    setDirectionsCoordinates({ listItem, action: 'remove' })
                                }
                                aria-label='Remove Stop'
                                minW='none'
                                maxW={8}
                                ml={2}
                                w={8}
                            />
                        </div>
                    </ListItem>
                )
            })}
        </List>
    )
}

export default React.memo(RouteStopsList)
